import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Document Parts component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconDocumentParts extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.5996 15.377C16.383 15.377 17.0186 16.0125 17.0186 16.7959C17.0184 17.5791 16.3829 18.2139 15.5996 18.2139C14.8165 18.2136 14.1818 17.579 14.1816 16.7959C14.1816 16.0127 14.8164 15.3772 15.5996 15.377Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M18.0273 11.501C18.3651 11.5011 18.6768 11.6811 18.8457 11.9736L21.373 16.3496C21.5419 16.6421 21.5419 17.0034 21.373 17.2959L18.8457 21.6738L18.7764 21.7773C18.5988 22.008 18.3221 22.1465 18.0264 22.1465L12.9727 22.1455L12.8477 22.1377C12.5592 22.0992 12.3021 21.9289 12.1543 21.6729L9.62695 17.2969C9.47912 17.0408 9.46037 16.7319 9.57129 16.4629L9.62695 16.3506L12.1543 11.9727C12.3232 11.6805 12.6352 11.5002 12.9727 11.5L18.0273 11.501ZM13.2461 12.9189L10.9922 16.8223L13.2461 20.7275L17.7549 20.7266L20.0078 16.8232L17.7549 12.918L13.2461 12.9189Z"
          />
          <path
            d="M11.7793 1.13965C11.9788 1.13975 12.1704 1.21925 12.3115 1.36035L19.4248 8.47363C19.5659 8.61476 19.6445 8.80633 19.6445 9.00586V9.25684C19.6445 9.67244 19.3081 10.0095 18.8926 10.0098C18.4983 10.0098 18.174 9.70625 18.1416 9.32031L11.4668 2.64551H4.66602C4.48847 2.64551 4.31797 2.71631 4.19238 2.8418C4.0668 2.96738 3.99612 3.13783 3.99609 3.31543V18.9648C3.99618 19.1424 4.06686 19.3129 4.19238 19.4385C4.31796 19.5639 4.48853 19.6338 4.66602 19.6338H7.44238C7.85804 19.6338 8.19508 19.9711 8.19531 20.3867C8.19531 20.8025 7.85818 21.1396 7.44238 21.1396H4.66602C4.08903 21.1396 3.53494 20.9109 3.12695 20.5029C2.71916 20.095 2.49032 19.5416 2.49023 18.9648V3.31543C2.49026 2.73863 2.71918 2.18528 3.12695 1.77734C3.53494 1.36936 4.08904 1.13965 4.66602 1.13965H11.7793Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-document-parts') === undefined) {
  customElements.define('cat-icon-document-parts', BlocksIconDocumentParts);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-document-parts': BlocksIconDocumentParts;
  }
}
